'use client';

import { MinusIcon, PlusIcon } from '@/components/icons';
import type { CSSProperties } from 'react';
import type { ProductWithCategories } from '@/lib/api/products';
import type { StorefrontArt } from '@/lib/api/storefront-media';
import { formatPrice } from '@/lib/utils';
import { isOrderable, productPresentation } from './catalog-model';
import { ProductStickers } from './product-stickers';

type Props = {
  product: ProductWithCategories;
  art?: StorefrontArt;
  quantity: number;
  wide?: boolean;
  eager?: boolean;
  onOpen: () => void;
  onAdd: () => void;
  onQuantity: (quantity: number) => void;
};

export function CatalogCard({
  product,
  art,
  quantity,
  wide,
  eager,
  onOpen,
  onAdd,
  onQuantity,
}: Props) {
  const { image, scene, studioRetouched } = productPresentation(product, art);
  const available = isOrderable(product);
  const sceneStyle = scene
    ? ({
        '--scene-image': `url("${scene.image}")`,
        '--card-color': scene.color,
        color: scene.ink,
      } as CSSProperties)
    : undefined;
  return (
    <article
      data-product-id={product.id}
      className={`product-card ${scene ? 'scene-card scene-master' : 'plain'}${wide && scene ? ' mobile-wide-hit' : ''}${!available ? ' is-unavailable' : ''}`}
      style={sceneStyle}
    >
      <div className="card-backdrop" aria-hidden="true">
        <div className="card-texture" />
      </div>
      <button
        className="food-button"
        onClick={onOpen}
        aria-label={`Подробнее: ${product.name.trim()}`}
      >
        <img
          className={`product-image ${scene ? 'scene-foreground' : studioRetouched ? 'studio-photo cutout' : 'catalog-api-photo'}`}
          src={scene?.foreground || image}
          alt={product.name.trim()}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
        />
      </button>
      <ProductStickers product={product} />
      <div className="card-copy">
        <h3>
          <button onClick={onOpen}>{product.name.trim()}</button>
        </h3>
        {wide && scene && <p className="wide-hit-description">{product.description}</p>}
        <div className="card-actions">
          {quantity ? (
            <div className="stepper">
              <button
                aria-label={`Убрать один ${product.name.trim()}`}
                onClick={() => onQuantity(quantity - 1)}
              >
                <MinusIcon size={16} />
              </button>
              <span>{quantity}</span>
              <button
                aria-label={`Добавить один ${product.name.trim()}`}
                onClick={onAdd}
                disabled={!available || quantity >= 99}
              >
                <PlusIcon size={16} />
              </button>
            </div>
          ) : (
            <button
              className="price-pill"
              onClick={onAdd}
              disabled={!available}
              aria-label={
                available
                  ? `В корзину: ${product.name.trim()}, ${formatPrice(product.price)}`
                  : `${product.name.trim()} — скоро в меню`
              }
            >
              {available ? formatPrice(product.price) : 'Скоро'}
              {available && <PlusIcon className="price-plus" size={15} />}
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
